import React from 'react';
import { Button } from '@/components/ui/button';
import { MapPin, Phone, Mail, CalendarClock, Globe, ArrowRight, Heart, ExternalLink } from 'lucide-react';
import { CONTACT } from '@/constants/testIds';
import { AAWTA } from '@/lib/aawta';

const openExternal = (url) => window.open(url, '_blank', 'noopener,noreferrer');

export const Contact = ({ onOpenInquiry }) => {
  return (
    <section id="contact" className="py-24 md:py-32 bg-aawta-surface" data-testid={CONTACT.section}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="overline text-aawta-magenta">Get in Touch</span>
            <h2 className="font-display font-bold text-4xl md:text-5xl mt-4 text-aawta-navy tracking-tight leading-[1.1]">
              Let’s build the workforce of the future — together.
            </h2>
            <div className="divider-magenta mt-6" />
            <p className="mt-6 text-[17px] leading-relaxed text-aawta-muted">
              Whether you’re exploring sponsorship, attendance, or partnership, our team is
              ready to talk. Book a meeting, send us an inquiry, or make a gift — every
              contribution moves the movement forward.
            </p>

            <div className="mt-8 space-y-3">
              <Button
                data-testid={CONTACT.scheduleMeeting}
                onClick={() => openExternal(AAWTA.calendarUrl)}
                className="w-full sm:w-auto rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white font-medium px-7 py-6 shadow-aawta-magenta hover:-translate-y-0.5 transition-all group"
              >
                <CalendarClock className="mr-2 h-4 w-4" />
                Schedule a Meeting
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>

              {/* Support the Mission block */}
              <div className="mt-4 rounded-2xl bg-white border border-aawta-border p-5 shadow-aawta-soft">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-aawta-magenta/10 text-aawta-magenta flex items-center justify-center shrink-0">
                    <Heart className="h-5 w-5" fill="currentColor" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-display font-semibold text-lg text-aawta-navy">
                      Support the Mission
                    </h4>
                    <p className="text-sm text-aawta-muted mt-1 leading-relaxed">
                      The Power to Drive Change: Transforming Our Community for a Better
                      Tomorrow!
                    </p>
                    <Button
                      data-testid={CONTACT.supportMission}
                      onClick={() => openExternal(AAWTA.zeffyDonateUrl)}
                      className="mt-4 rounded-full bg-aawta-navy hover:bg-aawta-navy-deep text-white font-medium px-6"
                    >
                      Support the Mission
                      <ExternalLink className="ml-2 h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-2">
                <Button
                  data-testid="contact-inquiry-cta"
                  onClick={() => onOpenInquiry('contact')}
                  variant="outline"
                  className="rounded-full border-aawta-navy text-aawta-navy hover:bg-aawta-navy hover:text-white font-medium px-6 py-6"
                >
                  Send an Inquiry
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            <div className="bg-white rounded-2xl p-7 border border-aawta-border shadow-aawta-soft hover:shadow-aawta-elevated transition-all duration-300">
              <div className="w-11 h-11 rounded-xl bg-aawta-navy text-white flex items-center justify-center">
                <MapPin className="h-5 w-5" />
              </div>
              <div className="overline mt-5 text-aawta-muted">AAWTA Office</div>
              <p className="mt-2 text-[15px] font-medium text-aawta-navy">
                African American Women Trucking Association
              </p>
              <p className="mt-1 text-sm text-aawta-muted leading-relaxed">
                5269 Buford Highway<br />
                Doraville, GA 30340
              </p>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(AAWTA.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-map-link"
                className="mt-4 inline-flex items-center text-sm font-semibold text-aawta-magenta hover:text-aawta-magenta-hover"
              >
                Get directions
                <ArrowRight className="ml-1 h-4 w-4" />
              </a>
            </div>

            <div className="bg-white rounded-2xl p-7 border border-aawta-border shadow-aawta-soft hover:shadow-aawta-elevated transition-all duration-300">
              <div className="w-11 h-11 rounded-xl bg-aawta-navy text-white flex items-center justify-center">
                <Phone className="h-5 w-5" />
              </div>
              <div className="overline mt-5 text-aawta-muted">Phone</div>
              <a
                href={`tel:${AAWTA.phoneRaw}`}
                data-testid={CONTACT.phoneLink}
                className="mt-2 inline-block text-[17px] font-semibold text-aawta-navy hover:text-aawta-magenta"
              >
                {AAWTA.phone}
              </a>
              <p className="mt-1 text-sm text-aawta-muted">Mon – Fri · 9am – 5pm ET</p>
            </div>

            <div className="bg-white rounded-2xl p-7 border border-aawta-border shadow-aawta-soft hover:shadow-aawta-elevated transition-all duration-300">
              <div className="w-11 h-11 rounded-xl bg-aawta-navy text-white flex items-center justify-center">
                <Mail className="h-5 w-5" />
              </div>
              <div className="overline mt-5 text-aawta-muted">Email</div>
              <a
                href={`mailto:${AAWTA.email}`}
                data-testid={CONTACT.emailLink}
                className="mt-2 inline-block text-[17px] font-semibold text-aawta-navy hover:text-aawta-magenta break-all"
              >
                {AAWTA.email}
              </a>
              <p className="mt-1 text-sm text-aawta-muted">Sponsorship, media & partnerships</p>
            </div>

            <div className="bg-aawta-navy rounded-2xl p-7 text-white relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-aawta-magenta/25 blur-2xl" />
              <div className="relative">
                <div className="w-11 h-11 rounded-xl bg-white/10 text-white flex items-center justify-center">
                  <Globe className="h-5 w-5" />
                </div>
                <div className="overline mt-5 text-white/70">On the Web</div>
                <a
                  href={AAWTA.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-website-link"
                  className="mt-2 inline-block text-[17px] font-semibold hover:text-aawta-magenta"
                >
                  www.aawta.org
                </a>
                <p className="mt-1 text-sm text-white/70">
                  The African American Women Trucking Association
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
